/* Map self-healing: detect vector (OpenFreeMap/MapLibre) failures, remember the Esri fallback for the session, expose diagnostics. */
export const MAP_CACHES = ["dropit-tiles-vector-v2", "dropit-tiles-raster-v2"];
const PREF_KEY = "dropit-map-provider";
const DIAG_KEY = "__dropitMapDiag";

export const vectorDisabled = () => {
  try {
    const p = JSON.parse(sessionStorage.getItem(PREF_KEY) || "null");
    return !!p && p.provider === "esri" && new Date(p.at).toDateString() === new Date().toDateString();
  } catch { return false; }
};
export const rememberFallback = (diag) => { try { sessionStorage.setItem(PREF_KEY, JSON.stringify({ provider: "esri", at: new Date().toISOString(), reason: diag?.reason })); } catch { /* private mode */ } };
export const forgetFallback = () => { try { sessionStorage.removeItem(PREF_KEY); } catch { /* ignore */ } };

export const clearMapCaches = async () => {
  forgetFallback();
  if (!("caches" in window)) return;
  await Promise.all(MAP_CACHES.map((n) => caches.delete(n).catch(() => false)));
};

const hostOf = (u) => { try { return new URL(u).host; } catch { return ""; } };
export const publishDiag = (d) => {
  const diag = { ...d, at: new Date().toISOString() };
  window[DIAG_KEY] = [...(window[DIAG_KEY] || []).slice(-9), diag];
  console.warn("[dropit-map] provider fallback", JSON.stringify(diag));
  return diag;
};

/* Read a few pixels from the live WebGL canvas during the render callback (buffer still valid without preserveDrawingBuffer). */
export const canvasIsBlank = (canvas) => {
  try {
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (!gl || gl.isContextLost()) return true;
    const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight; if (!w || !h) return true;
    const px = new Uint8Array(4); let dark = 0; const pts = [[0.5, 0.5], [0.2, 0.3], [0.8, 0.3], [0.2, 0.7], [0.8, 0.7], [0.5, 0.15], [0.5, 0.85]];
    for (const [fx, fy] of pts) { gl.readPixels(Math.floor(w * fx), Math.floor(h * fy), 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); if (px[0] + px[1] + px[2] < 24) dark += 1; }
    return dark === pts.length;
  } catch { return false; }  // cannot sample → don't punish a working map
};

/* Watches a MapLibre map: style/glyph/sprite failures, ≥3 tile failures, or no rendered tile within `timeoutMs` → onFail(diag). */
export const attachMapHealth = (m, { onFail, onReady, timeoutMs = 6000 }) => {
  let failed = 0, done = false, firstErr = null, ready = false;
  const finish = (reason) => {
    if (done) return; done = true; clearTimeout(timer);
    onFail?.(publishDiag({ provider: "openfreemap", reason, failedResources: failed, firstErrorStatus: firstErr?.status ?? null, firstErrorHost: firstErr?.host ?? null }));
  };
  const onError = (e) => {
    const err = e?.error || {}; const msg = String(err.message || ""); const url = err.url || e?.source?.url || "";
    if (!firstErr) firstErr = { status: err.status ?? null, host: hostOf(url) };
    if (/glyph|sprite|font/i.test(msg + url)) return finish("glyphs");
    if (!m.isStyleLoaded?.() && /style|json|webgl|context|fetch|network/i.test(msg) && !e.tile && !e.sourceId) return finish("style");
    if (e.tile || e.sourceId || /tile/i.test(msg + url)) { failed += 1; if (failed >= 3) finish("tiles"); }
  };
  const onIdle = () => {
    if (ready || done) return;
    if (!(m.isStyleLoaded() && m.areTilesLoaded())) return;
    const features = m.queryRenderedFeatures().length;
    if (features === 0) return finish("no-features");  // tiles "loaded" but nothing to draw (blocked/farbled)
    // GPU check (Brave Shields can farble WebGL so everything draws black): sample pixels right after a frame is drawn
    m.once("render", () => {
      if (done) return;
      if (canvasIsBlank(m.getCanvas())) return finish("blank-render");
      ready = true; clearTimeout(timer); onReady?.();  // keep listening: glyph/sprite/tile failures after first paint still trigger the fallback
    });
    m.triggerRepaint();
  };
  const timer = setTimeout(() => { if (!ready) finish(m.isStyleLoaded?.() ? "no-tiles" : "timeout"); }, timeoutMs);
  m.on("error", onError); m.on("idle", onIdle);  // context loss is handled by the owner (re-create the map), not as a provider failure
  return () => { clearTimeout(timer); m.off("error", onError); m.off("idle", onIdle); };
};

/* Liberty style's background is near-black: paint it app-surface grey so a slow/failed load never looks like "black tiles". */
export const greyBackground = (m) => {
  try { (m.getStyle()?.layers || []).filter((l) => l.type === "background").forEach((l) => m.setPaintProperty(l.id, "background-color", "#141a22")); } catch { /* style not ready */ }
};

/* Leaflet raster layer health: ≥3 tile errors before any tile loads → onFail; first tileload → onReady. */
export const attachRasterHealth = (layer, { onFail, onReady, timeoutMs = 8000 }) => {
  let errors = 0, ready = false, done = false;
  const finish = (reason, status) => { if (done) return; done = true; clearTimeout(timer); onFail?.(publishDiag({ provider: "esri", reason, failedResources: errors, firstErrorStatus: status ?? null, firstErrorHost: "static-map-tiles-api.arcgis.com" })); };
  layer.on("tileerror", () => { errors += 1; if (!ready && errors >= 3) finish("tiles"); });
  layer.on("tileload", () => { if (!ready) { ready = true; done = true; clearTimeout(timer); onReady?.(); } });
  const timer = setTimeout(() => { if (!ready) finish("timeout"); }, timeoutMs);
  return () => clearTimeout(timer);
};
